import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar as CalIcon, MapPin, Trophy, Wallet, FileText, Users, Activity, Cake, PartyPopper } from "lucide-react";
import { Link } from "react-router-dom";
import { isAdminLike } from "@/lib/roles";

const fmtDate = (iso) => new Date(iso).toLocaleString("es-CL", { dateStyle: "medium", timeStyle: "short" });

const BIRTHDAY_MESSAGES = [
  "¡Hoy es el día de {name}! Que esta nueva vuelta al sol esté llena de touchdowns y victorias.",
  "Feliz cumple, {name}. Que el campo te siga regalando momentos inolvidables.",
  "{name}, hoy celebramos contigo. Que tu próxima jugada sea la mejor de tu carrera.",
  "Un año más en el equipo, {name}. Eres parte del corazón de los Ferroviarios — ¡a brillar!",
  "¡Felicidades, {name}! Que la disciplina, el esfuerzo y el cariño del equipo te acompañen siempre.",
  "Hoy se viste de gala el roster: es el cumpleaños de {name}. ¡Imparable dentro y fuera de la cancha!",
  "{name}, gracias por dejar todo en cada práctica. Que tu cumpleaños sea tan grande como tu juego.",
];

const pickBirthdayMessage = (name) => {
  const msg = BIRTHDAY_MESSAGES[Math.floor(Math.random() * BIRTHDAY_MESSAGES.length)];
  return msg.replace("{name}", name);
};

export default function Dashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [dues, setDues] = useState([]);
  const [stats, setStats] = useState(null);
  const [birthdays, setBirthdays] = useState([]);

  useEffect(() => {
    api.get("/events").then(r => setEvents(r.data)).catch(()=>{});
    api.get("/birthdays").then(r => setBirthdays(r.data)).catch(()=>{});
    if (user?.role === "player") {
      api.get("/dues/me").then(r => setDues(r.data)).catch(()=>{});
    } else {
      api.get("/stats/club").then(r => setStats(r.data)).catch(()=>{});
    }
  }, [user]);

  const upcoming = events.filter(e => new Date(e.starts_at) >= new Date()).slice(0, 4);
  const nextEvent = upcoming[0];
  const pendingDues = dues.filter(d => d.status === "pending");
  const todayBirthdays = useMemo(() => birthdays.filter(b => b.is_today), [birthdays]);
  const upcomingBirthdays = useMemo(() => birthdays.filter(b => !b.is_today).slice(0, 4), [birthdays]);
  const todayMessages = useMemo(
    () => todayBirthdays.map(b => ({ ...b, message: pickBirthdayMessage(b.name) })),
    [todayBirthdays]
  );

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1600px]">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="overline">Sala de Control</div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-none mt-1">
              HOLA, {user?.name?.split(" ")[0]?.toUpperCase()}
            </h1>
          </div>
          <div className="hidden sm:block">
            <span className={`badge badge-${user?.role}`} data-testid="role-badge">{user?.role}</span>
          </div>
        </div>

        {/* Birthday banner (today) */}
        {todayMessages.length > 0 && (
          <div
            className="tactical-card mb-6 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #0A2540 0%, #102E4F 60%, #0A2540 100%)",
              borderColor: "#FACC15",
            }}
            data-testid="bday-banner"
          >
            {/* Subtle decorative diagonal stripe — Ferroviarios identity (azul + amarillo) */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-[0.08]"
              style={{
                backgroundImage: "repeating-linear-gradient(135deg, #FACC15 0px, #FACC15 2px, transparent 2px, transparent 22px)",
              }}
            />
            {todayMessages.map((b, idx) => (
              <div
                key={b.user_id}
                className={`relative flex items-center gap-3 sm:gap-4 px-4 py-3 sm:px-5 sm:py-4 ${idx > 0 ? "border-t border-[#FACC15]/20" : ""}`}
              >
                <div
                  className="shrink-0 flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full"
                  style={{ background: "rgba(250,204,21,0.12)", border: "1px solid rgba(250,204,21,0.4)" }}
                  aria-hidden
                >
                  <PartyPopper size={18} className="text-[#FACC15] sm:hidden"/>
                  <PartyPopper size={22} className="text-[#FACC15] hidden sm:block"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display leading-tight text-white text-base sm:text-xl md:text-2xl">
                    ¡FELIZ CUMPLEAÑOS,{" "}
                    <span className="text-[#FACC15]">{b.name.toUpperCase()}</span>!
                  </div>
                  {/* On small screens: only show age (keeps banner short). On md+: also show the long message. */}
                  {b.age && (
                    <div className="text-[10px] sm:text-xs text-[#FACC15]/80 mt-0.5 uppercase tracking-widest font-bold">
                      Cumple {b.age} años
                    </div>
                  )}
                  <div className="hidden md:block text-sm text-white/70 mt-1 leading-snug">{b.message}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hero banner */}
        <div className="relative tactical-card overflow-hidden mb-6 h-48 md:h-56">
          <img src="https://images.pexels.com/photos/35120075/pexels-photo-35120075.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="stadium" className="absolute inset-0 w-full h-full object-cover opacity-40"/>
          <div className="absolute inset-0 stadium-overlay" />
          <div className="relative h-full p-6 md:p-8 flex flex-col justify-end">
            <div className="overline text-[#A1A1AA]">Próximo evento</div>
            {nextEvent ? (
              <>
                <div className="font-display text-3xl md:text-4xl text-white leading-none mt-1">
                  {nextEvent.title.toUpperCase()}
                </div>
                <div className="text-[#A1A1AA] text-sm mt-2 flex items-center gap-4 flex-wrap">
                  <span className="flex items-center gap-1"><CalIcon size={14}/> {fmtDate(nextEvent.starts_at)}</span>
                  {nextEvent.location && <span className="flex items-center gap-1"><MapPin size={14}/> {nextEvent.location}</span>}
                </div>
              </>
            ) : (
              <div className="font-display text-2xl text-white">SIN EVENTOS PROGRAMADOS</div>
            )}
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-[#27272A] mb-6">
          {user?.role === "player" ? (
            <>
              <StatCell label="Cuotas pendientes" value={pendingDues.length} color={pendingDues.length ? "var(--warning)" : "var(--success)"}/>
              <StatCell label="Próximos eventos" value={upcoming.length}/>
              <StatCell label="Eventos totales" value={events.length}/>
              <StatCell label="Estado" value={pendingDues.length ? "AL DÍA" : "OK"} color="var(--accent)"/>
            </>
          ) : (
            <>
              <StatCell label="Jugadores" value={stats?.total_players ?? "—"} icon={Users}/>
              <StatCell label="Coaches" value={stats?.total_coaches ?? "—"} icon={Activity}/>
              <StatCell label="Próximos eventos" value={stats?.upcoming_events ?? "—"} icon={CalIcon}/>
              <StatCell label="Cuotas al día" value={stats?.dues_paid ?? "—"} color="var(--success)" icon={Wallet}/>
            </>
          )}
        </div>

        {/* Two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 tactical-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="overline">Próximos eventos</div>
              <Link to="/calendar" className="text-xs text-[#007AFF] uppercase tracking-widest">Ver todos →</Link>
            </div>
            <div className="space-y-0 border-t border-[#27272A]">
              {upcoming.length === 0 && <div className="text-[#71717A] py-6 text-sm">Sin eventos próximos.</div>}
              {upcoming.map(ev => (
                <div key={ev.id} className="py-4 border-b border-[#27272A] flex items-start justify-between gap-4" data-testid={`event-row-${ev.id}`}>
                  <div>
                    <div className="overline text-[#71717A]">{ev.event_type}</div>
                    <div className="font-display text-xl text-white">{ev.title.toUpperCase()}</div>
                    <div className="text-xs text-[#A1A1AA] mt-1 flex flex-wrap gap-3">
                      <span>{fmtDate(ev.starts_at)}</span>
                      {ev.location && <span>{ev.location}</span>}
                      {ev.opponent && <span>vs {ev.opponent}</span>}
                    </div>
                  </div>
                  {ev.meeting_url && (
                    <a href={ev.meeting_url} target="_blank" rel="noreferrer" className="btn-ghost text-xs">UNIRSE</a>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {/* Birthdays card */}
            <div className="tactical-card p-6" data-testid="bday-card">
              <div className="flex items-center justify-between mb-4">
                <div className="overline flex items-center gap-2"><Cake size={12} className="text-[#FF9F0A]"/> Próximos cumpleaños</div>
              </div>
              {upcomingBirthdays.length === 0 && todayBirthdays.length === 0 ? (
                <div className="text-[#71717A] text-xs py-2">Sin cumpleaños próximos.</div>
              ) : (
                <div className="space-y-3">
                  {upcomingBirthdays.map(b => (
                    <div key={b.user_id} className="flex items-center justify-between gap-3 border-b border-[#27272A] pb-3 last:border-b-0 last:pb-0">
                      <div className="flex items-center gap-3 min-w-0">
                        {b.picture ? (
                          <img src={b.picture} alt="" className="w-9 h-9 object-cover flex-shrink-0"/>
                        ) : (
                          <div className="w-9 h-9 bg-[#1A1A1A] border border-[#27272A] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{b.name?.[0]}</div>
                        )}
                        <div className="min-w-0">
                          <div className="text-sm text-white truncate font-bold">{b.name}</div>
                          <div className="text-[10px] text-[#A1A1AA] uppercase tracking-wider">{b.month_day}</div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="font-display text-xl text-[#FF9F0A] leading-none">{b.days_until}</div>
                        <div className="text-[9px] text-[#71717A] uppercase tracking-wider">días</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Existing right column card */}
            <div className="tactical-card p-6">
              <div className="overline mb-4">{user?.role === "player" ? "Mis cuotas" : "Atajos"}</div>
              {user?.role === "player" ? (
                <div className="space-y-3">
                  {dues.length === 0 && <div className="text-[#71717A] text-sm">Sin cuotas registradas.</div>}
                  {dues.slice(0,5).map(d => (
                    <div key={d.id} className="flex items-center justify-between border-b border-[#27272A] pb-3">
                      <div>
                        <div className="font-display text-lg text-white">{d.month}</div>
                        <div className="text-xs text-[#A1A1AA]">${d.amount.toFixed(2)}</div>
                      </div>
                      <span className={`badge badge-${d.status}`}>{d.status === "paid" ? "Pagado" : "Pendiente"}</span>
                    </div>
                  ))}
                  <Link to="/dues" className="btn-primary w-full mt-2 inline-flex items-center justify-center">Ver todas</Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <Link to="/calendar" className="btn-ghost w-full flex items-center justify-between text-sm uppercase tracking-wider"><span className="flex items-center gap-2"><CalIcon size={14}/>Programar evento</span>→</Link>
                  <Link to="/documents" className="btn-ghost w-full flex items-center justify-between text-sm uppercase tracking-wider"><span className="flex items-center gap-2"><FileText size={14}/>Subir documento</span>→</Link>
                  <Link to="/roster" className="btn-ghost w-full flex items-center justify-between text-sm uppercase tracking-wider"><span className="flex items-center gap-2"><Users size={14}/>Gestionar roster</span>→</Link>
                  {isAdminLike(user?.role) && (
                    <Link to="/admin" className="btn-ghost w-full flex items-center justify-between text-sm uppercase tracking-wider"><span className="flex items-center gap-2"><Trophy size={14}/>Panel admin</span>→</Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

const StatCell = ({ label, value, color, icon: Icon }) => (
  <div className="p-5 md:p-6 border-r border-b border-[#27272A] last:border-r-0">
    <div className="flex items-center justify-between">
      <div className="overline text-[#71717A]">{label}</div>
      {Icon && <Icon size={14} className="text-[#71717A]"/>}
    </div>
    <div className="font-display text-3xl md:text-4xl mt-2" style={{ color: color || "#fff" }}>{value}</div>
  </div>
);
